/**
 * <testimonial-slider>
 *
 * A purpose-built slider for the testimonial section. We do NOT use Swiper
 * here because:
 *   1. Testimonials only ever show ONE slide at a time and fade between
 *      them via opacity — no horizontal transform, no track scrolling.
 *   2. Swiper's loop math + slidesPerView:1 + transform:none override (set
 *      by the existing `is-word-reveal-ready` CSS) caused Swiper to compute
 *      slide widths of 2^25 px (33,554,432 px), which leaked up the DOM
 *      and overflowed the viewport on mobile.
 *
 * This component exposes a Swiper-compatible surface (`this.swiper` with
 * `slideTo`, `slideToLoop`, `slidePrev`, `slideNext`, `on`, `off`,
 * `realIndex`, `activeIndex`, `params.loop`) so `component-testimonial-
 * spotlight.js` can attach exactly the way it already does for Swiper —
 * no changes to the spotlight controller's slide-change handling.
 *
 * Slide visibility is controlled by adding `swiper-slide-active` (matches
 * the existing CSS contract) and `is-active` to the current slide. The
 * existing CSS in `sections/testimonial.liquid` already stacks slides
 * absolutely and fades between them when the spotlight is in word-reveal
 * mode — that CSS keeps working unchanged.
 */
class TestimonialSlider extends HTMLElement {
  constructor() {
    super();
    this.slides = [];
    this.realIndex = 0;
    this.activeIndex = 0;
    this.params = { loop: false };
    this.prevButton = null;
    this.nextButton = null;
    this._listeners = { slideChange: [], snapIndexChange: [] };
    this._touchStartX = 0;
    this._touchStartY = 0;
    this._touching = false;

    this._onPrevClick = this._onPrevClick.bind(this);
    this._onNextClick = this._onNextClick.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
  }

  connectedCallback() {
    this.params.loop = this.dataset.loop === 'true';
    this.slides = Array.from(this.querySelectorAll('.grid-slider__item'));

    if (this.slides.length === 0) return;

    // Tag every slide with its source index so the spotlight controller
    // can resolve the active slide by `data-swiper-slide-index`.
    this.slides.forEach((slide, index) => {
      if (slide instanceof HTMLElement) {
        slide.setAttribute('data-swiper-slide-index', String(index));
      }
    });

    // Resolve nav buttons (IDs are passed via data attributes from the
    // section markup so they can live outside the slider element).
    const prevId = this.dataset.navPrev || '';
    const nextId = this.dataset.navNext || '';
    if (prevId) this.prevButton = document.getElementById(prevId);
    if (nextId) this.nextButton = document.getElementById(nextId);

    if (this.prevButton instanceof HTMLElement) {
      this.prevButton.addEventListener('click', this._onPrevClick);
      this.prevButton.hidden = false;
    }
    if (this.nextButton instanceof HTMLElement) {
      this.nextButton.addEventListener('click', this._onNextClick);
      this.nextButton.hidden = false;
    }

    this.addEventListener('touchstart', this._onTouchStart, { passive: true });
    this.addEventListener('touchend', this._onTouchEnd, { passive: true });
    this.addEventListener('keydown', this._onKeydown);

    this._renderSlideStates();
    this._updateNavState();

    // Expose self under the `.swiper` property so existing controllers
    // (testimonial-spotlight) attach the same way they do to grid-slider's
    // Swiper instance — they look for `slider.swiper` and call methods on it.
    this.swiper = this;

    // Mark as ready so any CSS depending on `swiper-initialized` (the
    // existing word-reveal stack/fade rules) activates. We also add the
    // `is-swiper-active` class to mirror grid-slider's contract for any
    // shared CSS that targets it.
    const swiperShell = this.querySelector('.grid-slider__swiper');
    if (swiperShell instanceof HTMLElement) {
      swiperShell.classList.add('swiper-initialized', 'is-ready');
    }
    this.classList.add('is-swiper-active');
  }

  disconnectedCallback() {
    if (this.prevButton instanceof HTMLElement) {
      this.prevButton.removeEventListener('click', this._onPrevClick);
    }
    if (this.nextButton instanceof HTMLElement) {
      this.nextButton.removeEventListener('click', this._onNextClick);
    }
    this.removeEventListener('touchstart', this._onTouchStart);
    this.removeEventListener('touchend', this._onTouchEnd);
    this.removeEventListener('keydown', this._onKeydown);

    this._listeners.slideChange = [];
    this._listeners.snapIndexChange = [];
    this.slides = [];
    this.swiper = null;
  }

  // ─── Swiper-compatible public API ───────────────────────────────

  /** Subscribe to a slide-change event. Mirrors `swiper.on`. */
  on(event, callback) {
    if (typeof callback !== 'function') return;
    if (!this._listeners[event]) this._listeners[event] = [];
    this._listeners[event].push(callback);
  }

  /** Unsubscribe. Mirrors `swiper.off`. */
  off(event, callback) {
    if (!this._listeners[event]) return;
    this._listeners[event] = this._listeners[event].filter((cb) => cb !== callback);
  }

  slideTo(index) {
    this._goToIndex(this._clampIndex(index));
  }

  /** Loop-aware variant. We don't need a separate calculation because we
   *  already wrap on next/prev when loop is on, and external callers pass
   *  source indices directly. */
  slideToLoop(index) {
    this._goToIndex(this._clampIndex(index));
  }

  slideNext() {
    let next = this.realIndex + 1;
    if (next >= this.slides.length) {
      if (!this.params.loop) return;
      next = 0;
    }
    this._goToIndex(next);
  }

  slidePrev() {
    let prev = this.realIndex - 1;
    if (prev < 0) {
      if (!this.params.loop) return;
      prev = this.slides.length - 1;
    }
    this._goToIndex(prev);
  }

  // ─── Internals ──────────────────────────────────────────────────

  _clampIndex(index) {
    const total = this.slides.length;
    if (total === 0) return 0;
    if (this.params.loop) {
      const mod = index % total;
      return mod < 0 ? mod + total : mod;
    }
    return Math.max(0, Math.min(total - 1, index));
  }

  _goToIndex(index) {
    if (index === this.realIndex) return;
    this.realIndex = index;
    this.activeIndex = index;
    this._renderSlideStates();
    this._updateNavState();
    this._emit('slideChange');
    this._emit('snapIndexChange');
  }

  _updateNavState() {
    const total = this.slides.length;
    const atStart = this.realIndex <= 0;
    const atEnd = this.realIndex >= total - 1;

    if (this.prevButton instanceof HTMLElement) {
      const shouldDisable = total <= 1 || (!this.params.loop && atStart);
      this.prevButton.disabled = shouldDisable;
      this.prevButton.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
    }

    if (this.nextButton instanceof HTMLElement) {
      const shouldDisable = total <= 1 || (!this.params.loop && atEnd);
      this.nextButton.disabled = shouldDisable;
      this.nextButton.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
    }
  }

  _renderSlideStates() {
    this.slides.forEach((slide, i) => {
      if (!(slide instanceof HTMLElement)) return;
      const isActive = i === this.realIndex;

      // Match grid-slider / Swiper's class contract so the existing
      // testimonial CSS targets these slides without any change.
      slide.classList.toggle('swiper-slide-active', isActive);
      slide.classList.toggle('swiper-slide-visible', isActive);
      slide.classList.toggle('is-active', isActive);
      slide.classList.toggle('is-revealed', isActive);

      // a11y: hide non-active slides from assistive tech and remove from
      // tab order so users don't tab into invisible content.
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      this._setSlideTabbable(slide, isActive);
    });
  }

  _setSlideTabbable(slide, tabbable) {
    const focusables = slide.querySelectorAll('a, button, input, select, textarea, [tabindex]');
    focusables.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (tabbable) {
        if (node.dataset._tsRestoreTab === '') {
          node.removeAttribute('tabindex');
        } else if (node.dataset._tsRestoreTab) {
          node.setAttribute('tabindex', node.dataset._tsRestoreTab);
        }
        delete node.dataset._tsRestoreTab;
      } else {
        if (!Object.prototype.hasOwnProperty.call(node.dataset, '_tsRestoreTab')) {
          const original = node.getAttribute('tabindex');
          node.dataset._tsRestoreTab = original === null ? '' : original;
        }
        node.setAttribute('tabindex', '-1');
      }
    });
  }

  _emit(event) {
    if (!this._listeners[event]) return;
    this._listeners[event].forEach((cb) => {
      try {
        cb();
      } catch (err) {
        // Swallow listener errors so one bad subscriber doesn't break others.
      }
    });
  }

  _onPrevClick(event) {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    this.slidePrev();
  }

  _onNextClick(event) {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    this.slideNext();
  }

  _onTouchStart(event) {
    if (!event.touches || !event.touches[0]) return;
    this._touching = true;
    this._touchStartX = event.touches[0].clientX;
    this._touchStartY = event.touches[0].clientY;
  }

  _onTouchEnd(event) {
    if (!this._touching) return;
    this._touching = false;
    const touch = (event.changedTouches && event.changedTouches[0]) || null;
    if (!touch) return;
    const dx = touch.clientX - this._touchStartX;
    const dy = touch.clientY - this._touchStartY;
    // Require a clear horizontal swipe — ignore mostly-vertical drags so
    // we don't intercept page scrolling.
    if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
    if (dx < 0) this.slideNext();
    else this.slidePrev();
  }

  _onKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.slideNext();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.slidePrev();
    }
  }
}

if (!customElements.get('testimonial-slider')) {
  customElements.define('testimonial-slider', TestimonialSlider);
}
